import { useFormikContext } from "formik";
import { useEffect } from "react";
import { listarCategoriasAmbientales } from "../api/General";
import { useCatalog } from "../context/Catalog";
import { useTramite } from "../context/Tramite";
import { isInvalid } from "../utils/forms";
import { showConfirmDialog } from "../utils/dialogs";
import "./css/components.css";
import { useTranslation } from "react-i18next";
import {
  crearCertificadoAptitudAmbiental,
  crearRemediaciones,
  crearSeguroAmbiental,
  withEfluentesLiquidos,
  withEmisionesGaseosas,
  withEquiposPCB,
  withResiduosPatogenicos,
  withResiduosPeligrosos,
  withResiduosSolidosUrbanos,
} from "../utils/data";
import { isEditable } from "../utils/tramite";
import Loader from "./controls/Loader";
import Grid from "./layout/Grid";
import Row from "./layout/Row";
import Col from "./layout/Col";
import QuestionField from "./controls/QuestionField";
import SelectField from "./controls/SelectField";
import NumberField from "./controls/NumberField";
import ObjectField from "./controls/ObjectField";
import TextField from "./controls/TextField";
import DateField from "./controls/DateField";
import MoneyField from "./controls/MoneyField";
import { useDialog } from "./controls/Dialog";
import { TipoTramite } from "../utils/enums";

export default function ImpactoAmbientalForm({ ...props }) {
  const { tramite } = useTramite();
  const tramiteForm = useFormikContext();
  const { t } = useTranslation();
  const categoriasCatalog = useCatalog(["categoriasAmbientales"], () =>
    listarCategoriasAmbientales({}),
  );
  const loading = categoriasCatalog.loading;
  const dialog = useDialog({ showing: false, variant: "warning" });

  const confirmableToggles = {
    conEfluentesLiquidos: {
      onToggle: withEfluentesLiquidos,
      titleKey: "tramiteForm.conEfluentesLiquidos.dialog.title",
      messageKey: "tramiteForm.conEfluentesLiquidos.dialog.message",
      acceptKey: "tramiteForm.conEfluentesLiquidos.dialog.accept",
      declineKey: "tramiteForm.conEfluentesLiquidos.dialog.decline",
    },
    conResiduosSolidosUrbanos: {
      onToggle: withResiduosSolidosUrbanos,
      titleKey: "tramiteForm.conResiduosSolidosUrbanos.dialog.title",
      messageKey: "tramiteForm.conResiduosSolidosUrbanos.dialog.message",
      acceptKey: "tramiteForm.conResiduosSolidosUrbanos.dialog.accept",
      declineKey: "tramiteForm.conResiduosSolidosUrbanos.dialog.decline",
    },
    conResiduosPeligrosos: {
      onToggle: withResiduosPeligrosos,
      titleKey: "tramiteForm.conResiduosPeligrosos.dialog.title",
      messageKey: "tramiteForm.conResiduosPeligrosos.dialog.message",
      acceptKey: "tramiteForm.conResiduosPeligrosos.dialog.accept",
      declineKey: "tramiteForm.conResiduosPeligrosos.dialog.decline",
    },
    conResiduosPatogenicos: {
      onToggle: withResiduosPatogenicos,
      titleKey: "tramiteForm.conResiduosPatogenicos.dialog.title",
      messageKey: "tramiteForm.conResiduosPatogenicos.dialog.message",
      acceptKey: "tramiteForm.conResiduosPatogenicos.dialog.accept",
      declineKey: "tramiteForm.conResiduosPatogenicos.dialog.decline",
    },
    conEquiposPCB: {
      onToggle: withEquiposPCB,
      titleKey: "tramiteForm.conEquiposPCB.dialog.title",
      messageKey: "tramiteForm.conEquiposPCB.dialog.message",
      acceptKey: "tramiteForm.conEquiposPCB.dialog.accept",
      declineKey: "tramiteForm.conEquiposPCB.dialog.decline",
    },
    conEmisionesGaseosas: {
      onToggle: withEmisionesGaseosas,
      titleKey: "tramiteForm.conEmisionesGaseosas.dialog.title",
      messageKey: "tramiteForm.conEmisionesGaseosas.dialog.message",
      acceptKey: "tramiteForm.conEmisionesGaseosas.dialog.accept",
      declineKey: "tramiteForm.conEmisionesGaseosas.dialog.decline",
    },
  };

  const handleConfirmToggleChange = (config) => (event) => {
    const checked = event.target.checked;
    const isDJG = tramiteForm.values?.tipo?.codigo === TipoTramite.DJG;
    if (!checked && isDJG) {
      showConfirmDialog(dialog, {
        title: t(config.titleKey),
        message: <p>{t(config.messageKey)}</p>,
        acceptLabel: t(config.acceptKey),
        declineLabel: t(config.declineKey),
        onAccept: () => {
          config.onToggle(tramiteForm, checked);
        },
      });
    } else {
      config.onToggle(tramiteForm, checked);
    }
  };

  const handleConEfluentesLiquidosChange = handleConfirmToggleChange(
    confirmableToggles.conEfluentesLiquidos,
  );
  const handleConResiduosSolidosUrbanosChange = handleConfirmToggleChange(
    confirmableToggles.conResiduosSolidosUrbanos,
  );
  const handleConResiduosPeligrososChange = handleConfirmToggleChange(
    confirmableToggles.conResiduosPeligrosos,
  );
  const handleConResiduosPatogenicosChange = handleConfirmToggleChange(
    confirmableToggles.conResiduosPatogenicos,
  );
  const handleConEquiposPCBChange = handleConfirmToggleChange(
    confirmableToggles.conEquiposPCB,
  );
  const handleConEmisionesGaseosasChange = handleConfirmToggleChange(
    confirmableToggles.conEmisionesGaseosas,
  );
  const handleConCertificadoISO14001Change = (event) => {
    tramiteForm.handleChange(event);
  };
  const handleConSeguroAmbientalChange = (event) => {
    tramiteForm.handleChange(event);
  };
  const handleConRiesgoExplosionChange = (event) => {
    tramiteForm.handleChange(event);
  };
  const handleConRiesgoIncendioChange = (event) => {
    tramiteForm.handleChange(event);
  };
  const handleConRiesgoRuidosChange = (event) => {
    tramiteForm.handleChange(event);
  };
  const handleConRiesgoSustanciasQuimicasChange = (event) => {
    tramiteForm.handleChange(event);
  };
  const handleConSitiosContaminadosChange = (event) => {
    tramiteForm.handleChange(event);
  };
  const handleConFreatimetrosChange = (event) => {
    tramiteForm.handleChange(event);
  };
  const handleConRemediacionesActivasChange = (event) => {
    tramiteForm.handleChange(event);
  };
  const handleSeguroAmbientalChange = (event) => {
    tramiteForm.handleChange(event);
  };
  const handleCategoriaAmbientalChange = (event) => {
    tramiteForm.handleChange(event);
  };
  const handleNivelComplejidadAmbientalChange = (event) => {
    tramiteForm.handleChange(event);
  };
  const handleConCertificadoAptitudAmbientalChange = (event) => {
    tramiteForm.handleChange(event);
  };
  const handleRemediacionesChange = (event) => {
    tramiteForm.handleChange(event);
  };
  const handleConTramiteCertificadoAptituAmbientalChange = (event) => {
    tramiteForm.handleChange(event);
  };
  const handleCertificadoAptitudAmbientalChange = (event) => {
    tramiteForm.handleChange(event);
  };
  useEffect(() => {
    if (!tramiteForm.values.conCertificadoAptitudAmbiental) {
      tramiteForm.setFieldValue("certificadoAptitudAmbiental", undefined, true);
    } else {
      if (!tramiteForm.values.certificadoAptitudAmbiental) {
        tramiteForm.setFieldValue(
          "certificadoAptitudAmbiental",
          tramite.certificadoAptitudAmbiental ||
            crearCertificadoAptitudAmbiental(),
          true,
        );
      }
      tramiteForm.setFieldValue(
        "conTramiteCertificadoAptitudAmbiental",
        false,
        true,
      );
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tramiteForm.values.conCertificadoAptitudAmbiental]);
  useEffect(() => {
    if (!tramiteForm.values.conSeguroAmbiental) {
      tramiteForm.setFieldValue("seguroAmbiental", undefined, true);
    } else if (!tramiteForm.values.seguroAmbiental) {
      tramiteForm.setFieldValue(
        "seguroAmbiental",
        crearSeguroAmbiental(),
        true,
      );
    } else {
      tramiteForm.setFieldValue(
        "seguroAmbiental",
        tramite.seguroAmbiental,
        true,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tramiteForm.values.conSeguroAmbiental]);
  useEffect(() => {
    if (!tramiteForm.values.conRemediacionesActivas) {
      tramiteForm.setFieldValue("remediaciones", undefined, true);
    } else if (!tramiteForm.values.remediaciones) {
      tramiteForm.setFieldValue("remediaciones", crearRemediaciones(), true);
    } else {
      tramiteForm.setFieldValue("remediaciones", tramite.remediaciones, true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tramiteForm.values.conRemediacionesActivas]);
  return (
    <Loader loading={loading}>
      {dialog.component}
      <Grid>
        <Row>
          <Col lg={12}>
            <QuestionField
              id="conEfluentesLiquidos"
              name="conEfluentesLiquidos"
              label={t("tramite.conEfluentesLiquidos")}
              value={tramiteForm.values.conEfluentesLiquidos}
              onChange={handleConEfluentesLiquidosChange}
              isInvalid={isInvalid("conEfluentesLiquidos", tramiteForm)}
              error={t(tramiteForm.errors.conEfluentesLiquidos)}
              disabled={!isEditable(tramite)}
            />
          </Col>
        </Row>
        <Row>
          <Col lg={12}>
            <QuestionField
              id="conResiduosSolidosUrbanos"
              name="conResiduosSolidosUrbanos"
              label={t("tramite.conResiduosSolidosUrbanos")}
              value={tramiteForm.values.conResiduosSolidosUrbanos}
              onChange={handleConResiduosSolidosUrbanosChange}
              isInvalid={isInvalid("conResiduosSolidosUrbanos", tramiteForm)}
              error={t(tramiteForm.errors.conResiduosSolidosUrbanos)}
              disabled={!isEditable(tramite)}
            />
          </Col>
        </Row>
        <Row>
          <Col lg={12}>
            <QuestionField
              id="conResiduosPeligrosos"
              name="conResiduosPeligrosos"
              label={t("tramite.conResiduosPeligrosos")}
              value={tramiteForm.values.conResiduosPeligrosos}
              onChange={handleConResiduosPeligrososChange}
              isInvalid={isInvalid("conResiduosPeligrosos", tramiteForm)}
              error={t(tramiteForm.errors.conResiduosPeligrosos)}
              disabled={!isEditable(tramite)}
            />
          </Col>
        </Row>
        <Row>
          <Col lg={12}>
            <QuestionField
              id="conResiduosPatogenicos"
              name="conResiduosPatogenicos"
              label={t("tramite.conResiduosPatogenicos")}
              value={tramiteForm.values.conResiduosPatogenicos}
              onChange={handleConResiduosPatogenicosChange}
              isInvalid={isInvalid("conResiduosPatogenicos", tramiteForm)}
              error={t(tramiteForm.errors.conResiduosPatogenicos)}
              disabled={!isEditable(tramite)}
            />
          </Col>
        </Row>
        <Row>
          <Col lg={12}>
            <QuestionField
              id="conEquiposPCB"
              name="conEquiposPCB"
              label={t("tramite.conEquiposPCB")}
              value={tramiteForm.values.conEquiposPCB}
              onChange={handleConEquiposPCBChange}
              isInvalid={isInvalid("conEquiposPCB", tramiteForm)}
              error={t(tramiteForm.errors.conEquiposPCB)}
              disabled={!isEditable(tramite)}
            />
          </Col>
        </Row>
        <Row>
          <Col lg={12}>
            <QuestionField
              id="conEmisionesGaseosas"
              name="conEmisionesGaseosas"
              label={t("tramite.conEmisionesGaseosas")}
              value={tramiteForm.values.conEmisionesGaseosas}
              onChange={handleConEmisionesGaseosasChange}
              isInvalid={isInvalid("conEmisionesGaseosas", tramiteForm)}
              error={t(tramiteForm.errors.conEmisionesGaseosas)}
              disabled={!isEditable(tramite)}
            />
          </Col>
        </Row>
        <Row>
          <Col lg={7}>
            <SelectField
              id="categoriaAmbiental"
              name="categoriaAmbiental"
              label={t("tramite.categoriaAmbiental")}
              value={tramiteForm.values.categoriaAmbiental}
              onChange={handleCategoriaAmbientalChange}
              options={categoriasCatalog.data}
              labelAttr="nombre"
              valueAttr="codigo"
              emptyLabel="..."
              isInvalid={isInvalid("categoriaAmbiental", tramiteForm)}
              error={t(tramiteForm.errors.categoriaAmbiental)}
              disabled={!isEditable(tramite)}
            />
          </Col>
        </Row>
        <Row>
          <Col lg={4}>
            <NumberField
              id="nivelComplejidadAmbiental"
              name="nivelComplejidadAmbiental"
              label={t("tramite.nivelComplejidadAmbiental")}
              value={tramiteForm.values.nivelComplejidadAmbiental}
              onChange={handleNivelComplejidadAmbientalChange}
              isInvalid={isInvalid("nivelComplejidadAmbiental", tramiteForm)}
              error={t(tramiteForm.errors.nivelComplejidadAmbiental)}
              disabled={!isEditable(tramite)}
            />
          </Col>
        </Row>
        <Row>
          <Col lg={12}>
            <QuestionField
              id="conCertificadoAptitudAmbiental"
              name="conCertificadoAptitudAmbiental"
              label={t("tramite.conCertificadoAptitudAmbiental")}
              value={tramiteForm.values.conCertificadoAptitudAmbiental}
              onChange={handleConCertificadoAptitudAmbientalChange}
              isInvalid={isInvalid(
                "conCertificadoAptitudAmbiental",
                tramiteForm,
              )}
              error={t(tramiteForm.errors.conCertificadoAptitudAmbiental)}
              disabled={!isEditable(tramite)}
            />
          </Col>
        </Row>
        <Row>
          <Col lg={12}>
            <QuestionField
              id="conTramiteCertificadoAptitudAmbiental"
              name="conTramiteCertificadoAptitudAmbiental"
              label={t("tramite.conTramiteCertificadoAptitudAmbiental")}
              value={tramiteForm.values.conTramiteCertificadoAptitudAmbiental}
              onChange={handleConTramiteCertificadoAptituAmbientalChange}
              isInvalid={isInvalid(
                "conTramiteCertificadoAptitudAmbiental",
                tramiteForm,
              )}
              error={t(
                tramiteForm.errors.conTramiteCertificadoAptitudAmbiental,
              )}
              disabled={
                tramiteForm.values.conCertificadoAptitudAmbiental ||
                !isEditable(tramite)
              }
            />
          </Col>
        </Row>
        <Row>
          <Col lg={11} style={{ marginLeft: "20px", marginRight: "15px" }}>
            <ObjectField
              id="certificadoAptitudAmbiental"
              name="certificadoAptitudAmbiental"
              value={tramiteForm.values.certificadoAptitudAmbiental}
              onChange={handleCertificadoAptitudAmbientalChange}
              disabled={
                !tramiteForm.values.conCertificadoAptitudAmbiental ||
                !isEditable(tramite)
              }
            >
              {({ value, setProperty, disabled }) => (
                <Grid>
                  <Row>
                    <Col lg={4}>
                      <TextField
                        id="certificadoAptitudAmbiental_numero"
                        name="certificadoAptitudAmbiental.numero"
                        label={t("certificadoAptitudAmbiental.numero")}
                        value={value?.numero}
                        onChange={(event) => {
                          event.preventDefault();
                          setProperty("numero", event.target.value);
                        }}
                        isInvalid={isInvalid(
                          "certificadoAptitudAmbiental.numero",
                          tramiteForm,
                        )}
                        error={t(
                          tramiteForm.errors.certificadoAptitudAmbiental
                            ?.numero,
                        )}
                        disabled={disabled}
                      />
                    </Col>
                    <Col lg={4}>
                      <TextField
                        id="certificadoAptitudAmbiental_disposicion"
                        name="certificadoAptitudAmbiental.disposicion"
                        label={t("certificadoAptitudAmbiental.disposicion")}
                        value={value?.disposicion}
                        onChange={(event) => {
                          event.preventDefault();
                          setProperty("disposicion", event.target.value);
                        }}
                        isInvalid={isInvalid(
                          "certificadoAptitudAmbiental.disposicion",
                          tramiteForm,
                        )}
                        error={t(
                          tramiteForm.errors.certificadoAptitudAmbiental
                            ?.disposicion,
                        )}
                        disabled={disabled}
                      />
                    </Col>
                  </Row>
                </Grid>
              )}
            </ObjectField>
          </Col>
        </Row>
        <Row>
          <Col lg={12}>
            <QuestionField
              id="conCertificadoISO14001"
              name="conCertificadoISO14001"
              label={t("tramite.conCertificadoISO14001")}
              value={tramiteForm.values.conCertificadoISO14001}
              onChange={handleConCertificadoISO14001Change}
              isInvalid={isInvalid("conCertificadoISO14001", tramiteForm)}
              error={t(tramiteForm.errors.conCertificadoISO14001)}
              disabled={!isEditable(tramite)}
            />
          </Col>
        </Row>
        <Row>
          <Col lg={12}>
            <QuestionField
              id="conSeguroAmbiental"
              name="conSeguroAmbiental"
              label={t("tramite.conSeguroAmbiental")}
              value={tramiteForm.values.conSeguroAmbiental}
              onChange={handleConSeguroAmbientalChange}
              isInvalid={isInvalid("conSeguroAmbiental", tramiteForm)}
              error={t(tramiteForm.errors.conSeguroAmbiental)}
              disabled={!isEditable(tramite)}
            />
          </Col>
        </Row>
        <Row>
          <Col lg={11} style={{ marginLeft: "20px", marginRight: "15px" }}>
            <ObjectField
              id="seguroAmbiental"
              name="seguroAmbiental"
              value={tramiteForm.values.seguroAmbiental}
              onChange={handleSeguroAmbientalChange}
              disabled={!isEditable(tramite)}
            >
              {({ value, setProperty, disabled }) => (
                <Grid>
                  <Row>
                    <Col lg={12}>
                      <TextField
                        id="seguroAmbiental_aseguradora"
                        name="seguroAmbiental.aseguradora"
                        label={t("seguroAmbiental.aseguradora")}
                        value={value?.aseguradora}
                        onChange={(event) => {
                          event.preventDefault();
                          setProperty("aseguradora", event.target.value);
                        }}
                        isInvalid={isInvalid(
                          "seguroAmbiental.aseguradora",
                          tramiteForm,
                        )}
                        error={t(
                          tramiteForm.errors.seguroAmbiental?.aseguradora,
                        )}
                        disabled={
                          !tramiteForm.values.conSeguroAmbiental || disabled
                        }
                      />
                    </Col>
                  </Row>
                  <Row>
                    <Col lg={4}>
                      <TextField
                        id="seguroAmbiental_poliza"
                        name="seguroAmbiental.poliza"
                        label={t("seguroAmbiental.poliza")}
                        value={value?.poliza}
                        onChange={(event) => {
                          event.preventDefault();
                          setProperty("poliza", event.target.value);
                        }}
                        isInvalid={isInvalid(
                          "seguroAmbiental.poliza",
                          tramiteForm,
                        )}
                        error={t(
                          tramiteForm.errors.seguroAmbiental?.aseguradora,
                        )}
                        disabled={
                          !tramiteForm.values.conSeguroAmbiental || disabled
                        }
                      />
                    </Col>
                    <Col lg={4}>
                      <DateField
                        id="seguroAmbiental_vencimiento"
                        name="seguroAmbiental.vencimiento"
                        label={t("seguroAmbiental.vencimiento")}
                        value={value?.vencimiento}
                        onChange={(event) => {
                          event.preventDefault();
                          setProperty("vencimiento", event.target.value);
                        }}
                        isInvalid={isInvalid(
                          "seguroAmbiental.vencimiento",
                          tramiteForm,
                        )}
                        error={t(
                          tramiteForm.errors.seguroAmbiental?.vencimiento,
                        )}
                        disabled={
                          !tramiteForm.values.conSeguroAmbiental || disabled
                        }
                      />
                    </Col>
                    <Col lg={4}>
                      <MoneyField
                        id="seguroAmbiental_importe"
                        name="seguroAmbiental.importe"
                        label={t("seguroAmbiental.importe")}
                        value={value?.importe}
                        onChange={(event) => {
                          event.preventDefault();
                          setProperty("importe", event.target.value);
                        }}
                        isInvalid={isInvalid(
                          "seguroAmbiental.importe",
                          tramiteForm,
                        )}
                        error={t(tramiteForm.errors.seguroAmbiental?.importe)}
                        disabled={
                          !tramiteForm.values.conSeguroAmbiental || disabled
                        }
                      />
                    </Col>
                  </Row>
                </Grid>
              )}
            </ObjectField>
          </Col>
        </Row>
        <Row>
          <Col lg={12}>
            <QuestionField
              id="conRiesgoExplosion"
              name="conRiesgoExplosion"
              label={t("tramite.conRiesgoExplosion")}
              value={tramiteForm.values.conRiesgoExplosion}
              onChange={handleConRiesgoExplosionChange}
              isInvalid={isInvalid("conRiesgoExplosion", tramiteForm)}
              error={t(tramiteForm.errors.conRiesgoExplosion)}
              disabled={!isEditable(tramite)}
            />
          </Col>
        </Row>
        <Row>
          <Col lg={12}>
            <QuestionField
              id="conRiesgoIncendio"
              name="conRiesgoIncendio"
              label={t("tramite.conRiesgoIncendio")}
              value={tramiteForm.values.conRiesgoIncendio}
              onChange={handleConRiesgoIncendioChange}
              isInvalid={isInvalid("conRiesgoIncendio", tramiteForm)}
              error={t(tramiteForm.errors.conRiesgoIncendio)}
              disabled={!isEditable(tramite)}
            />
          </Col>
        </Row>
        <Row>
          <Col lg={12}>
            <QuestionField
              id="conRiesgoRuidos"
              name="conRiesgoRuidos"
              label={t("tramite.conRiesgoRuidos")}
              value={tramiteForm.values.conRiesgoRuidos}
              onChange={handleConRiesgoRuidosChange}
              isInvalid={isInvalid("conRiesgoRuidos", tramiteForm)}
              error={t(tramiteForm.errors.conRiesgoRuidos)}
              disabled={!isEditable(tramite)}
            />
          </Col>
        </Row>
        <Row>
          <Col lg={12}>
            <QuestionField
              id="conRiesgoSustanciasQuimicas"
              name="conRiesgoSustanciasQuimicas"
              label={t("tramite.conRiesgoSustanciasQuimicas")}
              value={tramiteForm.values.conRiesgoSustanciasQuimicas}
              onChange={handleConRiesgoSustanciasQuimicasChange}
              isInvalid={isInvalid("conRiesgoSustanciasQuimicas", tramiteForm)}
              error={t(tramiteForm.errors.conRiesgoSustanciasQuimicas)}
              disabled={!isEditable(tramite)}
            />
          </Col>
        </Row>
        <Row>
          <Col lg={12}>
            <QuestionField
              id="conSitiosContaminados"
              name="conSitiosContaminados"
              label={t("tramite.conSitiosContaminados")}
              value={tramiteForm.values.conSitiosContaminados}
              onChange={handleConSitiosContaminadosChange}
              isInvalid={isInvalid("conSitiosContaminados", tramiteForm)}
              error={t(tramiteForm.errors.conSitiosContaminados)}
              disabled={!isEditable(tramite)}
            />
          </Col>
        </Row>
        <Row>
          <Col lg={12}>
            <QuestionField
              id="conFreatimetros"
              name="conFreatimetros"
              label={t("tramite.conFreatimetros")}
              value={tramiteForm.values.conFreatimetros}
              onChange={handleConFreatimetrosChange}
              isInvalid={isInvalid("conFreatimetros", tramiteForm)}
              error={t(tramiteForm.errors.conFreatimetros)}
              disabled={!isEditable(tramite)}
            />
          </Col>
        </Row>
        <Row>
          <Col lg={12}>
            <QuestionField
              id="conRemediacionesActivas"
              name="conRemediacionesActivas"
              label={t("tramite.conRemediacionesActivas")}
              value={tramiteForm.values.conRemediacionesActivas}
              onChange={handleConRemediacionesActivasChange}
              isInvalid={isInvalid("conRemediacionesActivas", tramiteForm)}
              error={t(tramiteForm.errors.conRemediacionesActivas)}
              disabled={!isEditable(tramite)}
            />
          </Col>
        </Row>
        <Row>
          <Col lg={11} style={{ marginLeft: "20px", marginRight: "15px" }}>
            <ObjectField
              id="remediaciones"
              name="remediaciones"
              value={tramiteForm.values.remediaciones}
              onChange={handleRemediacionesChange}
              disabled={!isEditable(tramite)}
            >
              {({ value, setProperty, disabled }) => (
                <Grid>
                  <Row>
                    <Col lg={12}>
                      <QuestionField
                        id="remediaciones_aprobadas"
                        name="remediaciones.aprobadas"
                        label={t("remediaciones.aprobadas")}
                        value={value?.aprobadas}
                        onChange={(event) => {
                          setProperty("aprobadas", event.target.checked);
                        }}
                        isInvalid={isInvalid(
                          "remediaciones.aprobadas",
                          tramiteForm,
                        )}
                        error={t(tramiteForm.errors.remediaciones?.aprobadas)}
                        disabled={
                          !tramiteForm.values.conRemediacionesActivas ||
                          disabled
                        }
                      />
                    </Col>
                  </Row>
                  <Row>
                    <Col lg={4}>
                      <TextField
                        id="remediaciones_disposicion"
                        name="remediaciones.disposicion"
                        label={t("remediaciones.disposicion")}
                        value={value?.disposicion}
                        onChange={(event) => {
                          setProperty("disposicion", event.target.value);
                        }}
                        isInvalid={isInvalid(
                          "remediaciones.disposicion",
                          tramiteForm,
                        )}
                        error={t(tramiteForm.errors.remediaciones?.disposicion)}
                        disabled={
                          !tramiteForm.values.conRemediacionesActivas ||
                          disabled
                        }
                      />
                    </Col>
                  </Row>
                </Grid>
              )}
            </ObjectField>
          </Col>
        </Row>
      </Grid>
    </Loader>
  );
}
